import type { QueryClient } from "@tanstack/react-query";

import type { LicenceStatus } from "../api/types";
import { k, locale, t } from "../i18n/i18n";
import { licenceScope } from "../state/licenceScope";
import { unsavedDrafts } from "../state/unsavedDrafts";

/** Clé de la requête d'état de licence, partagée par le portail et la barre latérale. */
export const LICENCE_QUERY = ["licence"] as const;

/** Trace du poste gardée par ce navigateur : son nom et sa dernière utilisation, sans identifiant ni code.
 *  Quand le serveur ne reconnaît plus le poste, elle montre que le navigateur a perdu le cookie du poste
 *  (navigation privée, cookies refusés ou effacés), au lieu de présenter l'écran comme un premier accès. */
const POSTE_TRACE_KEY = "generex:poste";

export interface PosteTrace {
  name: string | null;
  seenAt: string;
}

export function rememberPoste(status: LicenceStatus): void {
  try {
    const trace: PosteTrace = { name: status.device_name, seenAt: new Date().toISOString() };
    localStorage.setItem(POSTE_TRACE_KEY, JSON.stringify(trace));
  } catch {
    // Stockage refusé par le navigateur : pas de trace.
  }
}

export function forgetPoste(): void {
  try {
    localStorage.removeItem(POSTE_TRACE_KEY);
  } catch {
    // Stockage refusé : rien à oublier.
  }
}

export function previousPoste(): PosteTrace | null {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(POSTE_TRACE_KEY) ?? "null");
    if (!value || typeof value !== "object") return null;
    const { name, seenAt } = value as Record<string, unknown>;
    return typeof seenAt === "string" ? { name: typeof name === "string" && name ? name : null, seenAt } : null;
  } catch {
    return null;
  }
}

/** Autre licence dans ce navigateur, ou poste libéré : les données de la licence quittée sortent du cache et la page
 *  est rechargée, pour que rien d'elle ne s'affiche, même brièvement (audit de persistance m6). Ses brouillons ont
 *  déjà été effacés du navigateur (`licenceScope.adopt`, `licenceScope.release`) ; plus rien ne s'écrit ni ne se
 *  recopie jusqu'au rechargement. */
export function reloadForLicence(queryClient: QueryClient): void {
  licenceScope.freeze();
  unsavedDrafts.close();
  queryClient.clear();
  window.location.reload();
}

/** Ce que montre le portail de licence : attente de l'état, bascule vers une autre licence (rechargement), écran
 *  d'activation, notice, déclaration « Qui utilise ce poste ? » ou licence suspendue seuls (application jamais ouverte
 *  dans cette page), ou l'application — avec, quand la licence ou la place du poste est perdue en cours de session,
 *  l'un de ces écrans PAR-DESSUS (`overlay`), l'application restant montée avec ses saisies (audit de persistance M6). */
export type GateScreen = "activation" | "privacy" | "identity" | "suspended";

export interface GateState {
  view: "wait" | "switching" | GateScreen | "app";
  overlay: GateScreen | null;
}

/** Écran bloquant que demande l'état de la licence, dans l'ordre : activation, licence suspendue (rien d'autre ne
 *  sert tant qu'elle l'est), notice, puis déclaration de la personne qui utilise le poste. */
export function gateScreen(status: LicenceStatus): GateScreen | null {
  if (!status.enforced) return null;
  if (!status.licensed) return "activation";
  if (status.suspended) return "suspended";
  if (status.privacy_required) return "privacy";
  if (status.identity_required) return "identity";
  return null;
}

export function gateState(status: LicenceStatus | undefined, opened: boolean, switching: boolean): GateState {
  if (!status) return { view: "wait", overlay: null };
  if (switching) return { view: "switching", overlay: null };
  const screen = gateScreen(status);
  if (!opened) return { view: screen ?? "app", overlay: null };
  return { view: "app", overlay: screen };
}

/** Passage sous l'écran d'activation (« snapshot » : recopier les saisies dans le navigateur) ou retour à
 *  l'application (« resume » : relancer les envois en attente), d'un état du portail au suivant. */
export function overlayTransition(before: GateState["overlay"], after: GateState["overlay"]): "snapshot" | "resume" | null {
  if (!before && after) return "snapshot";
  if (before && !after) return "resume";
  return null;
}

/** Type de licence tel que l'iPad l'affiche : « 1 licence (1 terminal) », « 5 licences (5 terminaux) ». */
export function licenceTypeLabel(status: LicenceStatus): string {
  const n = status.max_devices ?? 1;
  return n <= 1 ? t("1 licence (1 terminal)") : t("{n} licences ({n} terminaux)", { n });
}

export function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(locale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
}

/** Date d'une échéance de licence expirée et de la fin de sa lecture seule, à l'heure de Paris comme le serveur (fin
 *  du délai : dernier instant d'une journée de Paris) : la même date pour tous les postes, quel que soit leur fuseau,
 *  et la même que dans les messages du serveur (revue du 03/10/2026). */
export function formatLicenceDay(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleDateString(locale(), { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Paris" })
    : "—";
}

/** Date longue, comme le « Expire le » de l'iPad (28 septembre 2027). */
export function formatLongDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(locale(), { day: "numeric", month: "long", year: "numeric" }) : "—";
}

export function formatDateTime(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString(locale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";
}

const CODE_SEGMENTS = [3, 3, 4, 4, 4];
const OFFLINE_PREFIX = "GENOFF";
const OFFLINE_CODE_LENGTH = 6 + 136;

/** Mise en forme du code pendant la saisie, comme `formatLicenceCode` de l'iPad : lettres et chiffres en
 *  majuscules, tirets aux limites 3-3-4-4-4 (GEN-REG-2026-XXXX-XXXX) ; codes hors ligne par groupes de cinq. */
export function formatLicenceCode(raw: string): string {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (cleaned.startsWith(OFFLINE_PREFIX)) {
    const capped = cleaned.slice(0, OFFLINE_CODE_LENGTH);
    const groups = capped.slice(OFFLINE_PREFIX.length).match(/.{1,5}/g) ?? [];
    return [OFFLINE_PREFIX, ...groups].join("-");
  }
  const capped = cleaned.slice(0, CODE_SEGMENTS.reduce((a, b) => a + b, 0));
  const parts: string[] = [];
  let start = 0;
  for (const length of CODE_SEGMENTS) {
    if (start >= capped.length) break;
    parts.push(capped.slice(start, start + length));
    start += length;
  }
  return parts.join("-");
}

export function isOfflineCode(code: string): boolean {
  return code.replace(/[^A-Za-z0-9]/g, "").toUpperCase().startsWith(OFFLINE_PREFIX);
}

/** Conseil ajouté au message du serveur de licences, selon le refus. */
export const ACTIVATION_HINTS: Record<string, string> = {
  LICENCE_NOT_FOUND: k("Vérifiez le code saisi (lettres, chiffres et tirets)."),
  DEVICE_LIMIT_REACHED: k(
    "Depuis un poste déjà activé, ouvrez la fiche de la licence (bas de la barre latérale) : « Révoquer l'accès » d'un poste qui ne sert plus, ou « Déplacer vers un autre poste… » ; sinon, demandez des places supplémentaires à BELLICOM.",
  ),
  LICENCE_EXPIRED: k("Contactez BELLICOM pour renouveler la licence."),
  LICENCE_REVOKED: k("Contactez BELLICOM."),
  LICENCE_SUSPENDED: k("Contactez BELLICOM."),
  OFFLINE_WINDOW_ELAPSED: k("Demandez un nouveau code hors ligne, ou utilisez le code de licence (GEN-…)."),
  OFFLINE_ALREADY_USED: k("Un code hors ligne n'ouvre qu'un seul poste : demandez-en un autre à BELLICOM."),
};
